import { useMemo } from 'react';
import { useCurrencyFormatter } from '@shared/utils/currency';
import { getCurrencyDecimalPlaces, minorToMajorUnits } from '@shared/utils/currency/minorUnits';
import { CouponPaidWithDtoEnum, DiscountTypeDtoEnum } from '@graphql/generated-base';
import type { TravelCashFundBestCoupon } from '@shared/hooks/useTravelCashFundBalance/useTravelCashFundBalance.types';
import { floorFundBalanceToWholeUnits } from './floorFundBalanceToWholeUnits';
import { DEFAULT_MIN_COUPON_SAVING_MAJOR_UNITS, isCouponSavingWorthShowing } from './isCouponSavingWorthShowing';
import type { PayoutBalance } from './cashFund.types';

export type FundCouponSavings = Readonly<{
  /**
   * The saving in minor units, already capped. Zero when no amount can be quoted yet — see
   * `CASH_FUND_AND_CARD` below, where the base is a stay the couple has not picked.
   *
   * An UPPER BOUND, never a promise. A `CASH_FUND` code is priced on the fund amount actually used,
   * which is the stay price when that is below the balance — so a $500 stay on an $1,100 fund saves
   * $35, not the $77 this figure describes.
   *
   * Promo copy leads with a concrete number by product decision. The honeymoon landing page uses
   * "up to" after product review, since the couple has not chosen a stay or fund amount yet.
   * This helper supplies the same estimate to both; checkout shows the actual applied discount.
   */
  minorUnits: number;
  /**
   * The code's percentage, when it has one AND the cap did not bite.
   *
   * Undefined on a fixed-amount code, and undefined once capped — a capped saving is no longer that
   * percentage of anything, so copy quoting the rate would promise one checkout will not honour.
   */
  percentOff?: number;
  /** The code itself. Render as sent — codes are renamed with no deploy, so never match on a name. */
  code?: string;
}>;

const NO_SAVINGS: FundCouponSavings = { minorUnits: 0 };

type FundCouponSavingsOptions = Readonly<{
  /** Keep the rate when the saving is under the minimum. Only the Manage Registry gift dialog opts in. */
  keepRateUnderMinimum?: boolean;
}>;

/**
 * Picks the code a fund surface may quote an AMOUNT for, and the one it may only quote a rate for.
 *
 * The two rules price on different bases, so they are not interchangeable:
 *
 * - `CASH_FUND` is priced on the fund amount used. On a page with no stay yet, that base IS the
 *   couple's balance, so "save an extra $77 on your $1,100" is a number we can stand behind.
 * - `CASH_FUND_AND_CARD` is priced on the whole stay total. Nothing here knows that total, so
 *   quoting 7% of the BALANCE against it would be a different, smaller number than the couple gets.
 *
 * `ANY` and `CARD` codes are never in this list — the graph only returns the two fund rules.
 */
const selectFundBasisCoupon = (coupons: ReadonlyArray<TravelCashFundBestCoupon>) => coupons.find(coupon => coupon.couponPaidWith === CouponPaidWithDtoEnum.CASH_FUND);
const selectStayBasisCoupon = (coupons: ReadonlyArray<TravelCashFundBestCoupon>) => coupons.find(coupon => coupon.couponPaidWith === CouponPaidWithDtoEnum.CASH_FUND_AND_CARD);

/**
 * What the couple's best cash-fund code saves on their balance.
 *
 * Everything here is the server's: the rate, the cap and the currency all come from
 * `bestCashFundCoupons`. It used to be a hard-coded 7% capped by a US-dollar flag figure, so a GBP
 * fund was capped in the wrong currency and every couple saw the same rate whatever code they had.
 *
 * Advisory only — no booking exists yet, so checkout still owns the final applied discount. The
 * amount describes the couple's whole balance spent, which is the BEST case: a stay cheaper than
 * the balance uses less fund and so saves less. Copy must present it as an upper bound, or tie it
 * to spending the full balance ("Use your {balance} … Save an extra {savings}").
 *
 * A saving under `minSavingMajorUnits` drops the amount and the rate but KEEPS the code — the code
 * is still redeemable, and the honeymoon page prints it whether or not a figure is quoted.
 * `keepRateUnderMinimum` keeps the rate there too, unless the cap bit.
 */
export const getFundCouponSavings = (
  balance: PayoutBalance,
  coupons: ReadonlyArray<TravelCashFundBestCoupon>,
  minSavingMajorUnits: number = DEFAULT_MIN_COUPON_SAVING_MAJOR_UNITS,
  options?: FundCouponSavingsOptions,
): FundCouponSavings => {
  const valueInMinorUnits = balance?.valueInMinorUnits;
  const hasUsableBalance = typeof valueInMinorUnits === 'number' && Number.isSafeInteger(valueInMinorUnits) && valueInMinorUnits > 0;

  const fundBasisCoupon = selectFundBasisCoupon(coupons);
  if (!fundBasisCoupon || !hasUsableBalance) {
    // No fund-basis code, so no amount. A split-basis code can still name its rate, which is the
    // one thing true about it without a stay.
    const stayBasisCoupon = selectStayBasisCoupon(coupons);
    if (!stayBasisCoupon || stayBasisCoupon.discountType !== DiscountTypeDtoEnum.PERCENTAGE || stayBasisCoupon.discountValue <= 0) return NO_SAVINGS;
    return { minorUnits: 0, percentOff: stayBasisCoupon.discountValue, code: stayBasisCoupon.code };
  }

  // Already in the fund's currency — the graph documents `currency` as the fund's — so it is never
  // converted. Null means uncapped.
  const capMinorUnits = fundBasisCoupon.maxDiscountAmountMinorUnits ?? undefined;

  if (fundBasisCoupon.discountType !== DiscountTypeDtoEnum.PERCENTAGE) {
    // No amount for a fixed-amount code. `discountValue` IS the discount, but it is in the coupon's
    // own currency and nothing here converts it; `maxDiscountAmountMinorUnits` is only a ceiling, so
    // substituting it would advertise a $10 code with a $100 cap as saving $100. The code's name is
    // still worth showing — the amount is not ours to guess.
    return { minorUnits: 0, code: fundBasisCoupon.code };
  }

  if (fundBasisCoupon.discountValue <= 0) return NO_SAVINGS;
  // Rounded DOWN, so the example never promises a fraction we cannot pay.
  const uncappedMinorUnits = Math.floor((valueInMinorUnits * fundBasisCoupon.discountValue) / 100);
  if (uncappedMinorUnits <= 0) return NO_SAVINGS;

  // The cap first, so the floor judges the figure the couple would actually be shown.
  const quotedMinorUnits = capMinorUnits && uncappedMinorUnits > capMinorUnits ? capMinorUnits : uncappedMinorUnits;
  const isCapped = quotedMinorUnits < uncappedMinorUnits;
  // Under the floor the amount and the rate go, but the code stays: it is still redeemable, and the
  // honeymoon page prints it under the CTA as proof the offer is real.
  if (!isCouponSavingWorthShowing(quotedMinorUnits, balance?.currencyCode, minSavingMajorUnits)) {
    // A capped saving is no longer that rate of anything, so the opt-in keeps an uncapped rate only.
    if (options?.keepRateUnderMinimum && !isCapped) return { minorUnits: 0, percentOff: fundBasisCoupon.discountValue, code: fundBasisCoupon.code };
    return { minorUnits: 0, code: fundBasisCoupon.code };
  }

  // Capped: the amount is still true, the rate is not — so the rate is dropped rather than shown.
  if (isCapped) return { minorUnits: quotedMinorUnits, code: fundBasisCoupon.code };
  return { minorUnits: quotedMinorUnits, percentOff: fundBasisCoupon.discountValue, code: fundBasisCoupon.code };
};

type UseFundCouponSavingsResult = Readonly<{
  /**
   * The saving, formatted in the fund's own currency — e.g. `"US$77"`. Undefined when no amount can
   * be quoted, which is what callers gate the money sentence on.
   */
  display?: string;
  /** The code's rate, only where it is still true. See `percentOff`. */
  percentOff?: number;
  /** The code itself. Render as sent — codes are renamed with no deploy, so never match on a name. */
  code?: string;
}>;

/**
 * That saving, ready to drop into a sentence.
 *
 * Same formatter and whole-unit flooring as `useFundBalanceDisplay`, so the saving and the balance
 * beside it in one sentence are drawn the same way — a line reading "$500 … $35.06" mixes two
 * precisions for no reason.
 */
export const useFundCouponSavings = (
  balance: PayoutBalance,
  coupons: ReadonlyArray<TravelCashFundBestCoupon>,
  minSavingMajorUnits: number = DEFAULT_MIN_COUPON_SAVING_MAJOR_UNITS,
  options?: FundCouponSavingsOptions,
): UseFundCouponSavingsResult => {
  const { formatCurrency } = useCurrencyFormatter();
  // A boolean dep, so an inline options object does not rebuild the memo every render.
  const keepRateUnderMinimum = !!options?.keepRateUnderMinimum;

  return useMemo(() => {
    const currencyCode = balance?.currencyCode;
    const { minorUnits, percentOff, code } = getFundCouponSavings(balance, coupons, minSavingMajorUnits, { keepRateUnderMinimum });
    if (!currencyCode || minorUnits <= 0) return { percentOff, code };

    const majorUnits = minorToMajorUnits(floorFundBalanceToWholeUnits(minorUnits, currencyCode), currencyCode);
    // Precision is stated, never left to the default 2: a sub-unit saving keeps its cents, and a
    // whole one drops the ".00" so it matches the balance it sits beside.
    const precision = Number.isInteger(majorUnits) ? 0 : getCurrencyDecimalPlaces(currencyCode);
    return {
      display: formatCurrency({
        priceFloatingPointDecimalString: majorUnits.toString(),
        priceCurrencyCode: currencyCode,
        formatForm: 'auto',
        formatPrecision: precision,
      }),
      percentOff,
      code,
    };
  }, [balance, coupons, formatCurrency, minSavingMajorUnits, keepRateUnderMinimum]);
};
